import React, {Dispatch, RefObject, SetStateAction, useEffect, useRef, useState} from "react";
import {Autocomplete, Box, IconButton, ListItemText, Popover, PopperProps, TextField, Tooltip} from "@mui/material";
import {AddRounded} from "@mui/icons-material";
import {useCase} from "../../provider/CaseProvider.tsx";
import {useAiChat} from "../../provider/AiChatProvider.tsx";

// Source of truth for slash skills is the Legalbird AI app (pdf_forms/definitions.py and the
// workflow skill markdown files); new skills must be mirrored here manually. Intentionally
// hardcoded: replace with an API call once we have more than 10 skills.
const AI_CHAT_SKILLS = [
  {
    command: "/vollstreckungsauftrag",
    label: "Vollstreckungsauftrag erstellen",
    description: "Vollstreckungsauftrag als PDF aus den Falldaten erstellen.",
    agents: ["LIA", "LEXA"],
  },
  {
    command: "/fristen",
    label: "Fristen notieren",
    description: "Haupt- und Vorfristen als Aktivitäten am Fall notieren.",
    agents: ["LIA", "LEXA"],
  },
  {
    command: "/verjährung",
    label: "Verjährung prüfen",
    description: "Verjährung von Ansprüchen prüfen und Fristaktivitäten vorbereiten.",
    agents: ["LEXA"],
  },
  {
    command: "/anspruchsgrundlagen",
    label: "Anspruchsgrundlagen prüfen",
    description: "Fallprüfung: Anspruchsgrundlagen im Vertragsrecht prüfen.",
    agents: ["LEXA"],
  },
];

export type AiChatSkillsProps = {
  setPrompt: Dispatch<SetStateAction<string>>
  prompt: string,
  textInputWrapperRef: RefObject<HTMLDivElement | null>
}

export default function AiChatSkills({ setPrompt, prompt, textInputWrapperRef }: AiChatSkillsProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLDivElement | null>(null);
  const { product } = useCase();
  const { activeAgent, isLoading } = useAiChat();

  if (!product) {
    return null;
  }

  useEffect(() => {
    // If you type / at the beginning, opens action menu.
    if (prompt?.match(/^\/(?!.+)/)) {
      setAnchorEl(textInputWrapperRef.current)
    }
  }, [prompt]);

  return (
    <>
      <Tooltip title={isLoading ? "Zum Auswählen einer Aktion zuerst die Eingabe leeren" : null}>
      <IconButton onClick={() => setAnchorEl(textInputWrapperRef.current)}>
        <AddRounded />
      </IconButton>
      </Tooltip>
      <Popover
        open={!!anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        disableScrollLock
        slotProps={{
          transition: {
            onEntered: (node: HTMLElement) => {
              const input = node.querySelector("input");
              input?.focus();
            },
          },
        }}
      >
        <Autocomplete
          open
          options={AI_CHAT_SKILLS.filter((aiSkill) => aiSkill.agents.includes(activeAgent))}
          onChange={(_, option) => {
            if (!option) {
              return;
            }
            setAnchorEl(null);
            setPrompt(prevPrompt => `${option.command} ${prevPrompt.replace(/^\//, "")}`);
          }}
          slots={{ popper: InlineAutocompletePopper }}
          sx={{ width: "22rem", margin: "0 auto" }}
          forcePopupIcon={false}
          slotProps={{
            paper: {
              elevation: 0,
            },
          }}
          renderInput={(params) => <TextField {...params} label="Aktion suchen" />}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <Box
                component={"li"}
                key={key}
                {...optionProps}
              >
                <ListItemText
                  primary={<strong>{option.label}</strong>}
                  secondary={option.description}
                  slotProps={{
                    primary: { variant: "body2" },
                    secondary: { variant: "caption" },
                  }}
                />
              </Box>
            );
          }}
        />
      </Popover>
    </>
  );
}

function InlineAutocompletePopper({ children, open, ref }: PopperProps) {
  if (!open) {
    return null;
  }
  const content = typeof children === "function" ? children({ placement: "bottom" }) : children;
  return <Box ref={ref}>{content}</Box>;
}
